/**
 * The FSSAI dietary marks — veg, non-veg and egg.
 *
 * Veg and non-veg are react-native-svg components (`./VegIcon`, `./NonVegIcon`), like every
 * other icon in this folder: `<Svg>` renders natively on mobile and as DOM `<svg>` through
 * react-native-svg's web build, so one component serves both platforms with no split. That is
 * different from importing a raw `.svg` FILE, which does need a split — Metro's
 * svg-transformer makes the default export a component, CRA's makes it a URL.
 *
 * Egg is still a PNG through RN's `<Image>` until it has vector artwork of its own. A default
 * `import`, not `require()`: Babel rewrites it to Metro's asset registry call on native and to
 * the file-loader URL on web, so the one specifier serves both.
 */
import React from "react";
import { Image } from "react-native";

// `@rista/core`'s own union, not a copy: `catalogFilter` narrows items with it and
// `kioskSlice` stores the active chip as it, so a second declaration here could drift from the
// vocabulary the catalog actually speaks. TYPE-ONLY, the single form of `@rista/core`
// dependency `packages/ui` allows (CLAUDE.md) — erased at build, no bundle edge.
import type { DietaryTag } from "@rista/core/catalog/types";
import EGG from "../png/EggIcon.png";
import { DietaryMark, type DietaryIconProps } from "./DietaryMark";
import { NonVegIcon } from "./NonVegIcon";
import { VegIcon } from "./VegIcon";

export type { DietaryIconProps };
export { NonVegIcon, VegIcon };

/** `contain`, so the mark is never cropped when the call site asks for an odd size. */
export function EggIcon({ size = 14 }: DietaryIconProps) {
  return (
    <DietaryMark size={size} label="Contains egg">
      <Image source={EGG} style={{ width: size, height: size }} resizeMode="contain" />
    </DietaryMark>
  );
}
EggIcon.displayName = "EggIcon";

export function DietaryIcon({ tag, size = 14 }: { tag: DietaryTag; size?: number }) {
  if (tag === "veg") return <VegIcon size={size} />;
  if (tag === "nveg") return <NonVegIcon size={size} />;
  if (tag === "egg") return <EggIcon size={size} />;
  return null;
}
DietaryIcon.displayName = "DietaryIcon";

export default DietaryIcon;
